/**
 * AzureAdAuthService — Microsoft Entra ID (Azure AD) SSO via MSAL.js
 */

import { PublicClientApplication } from '@azure/msal-browser';
import { getAzureConfig } from '../constants/AzureAdConfig.js';

let pca = null;

const MSAL_TIMEOUT_MS = 10000;

function getRedirectUri() {
    const custom = getAzureConfig('redirectUri');
    if (custom) return custom;
    return `${window.location.origin}/auth/callback`;
}

/** App root (login screen) — must also be registered in the Entra SPA redirect URIs. */
function getPostLogoutRedirectUri() {
    return `${window.location.origin}/`;
}

function isInteractionInProgressError(err) {
    const code = err?.errorCode || err?.code || '';
    const message = String(err?.message || err || '');
    return code === 'interaction_in_progress'
        || message.includes('interaction_in_progress')
        || message.includes('interaction is currently in progress');
}

async function withTimeout(promise, ms, label) {
    let timer;
    try {
        return await Promise.race([
            promise,
            new Promise((_, reject) => {
                timer = setTimeout(() => {
                    reject(new Error(`${label} timed out after ${ms}ms`));
                }, ms);
            })
        ]);
    } finally {
        clearTimeout(timer);
    }
}

export class AzureAdAuthService {
    static isEnabled() {
        return AzureAdAuthService.isConfigured();
    }

    static isConfigured() {
        return !!(getAzureConfig('tenantId') && getAzureConfig('clientId'));
    }

    static getApiScope() {
        return getAzureConfig('apiScope');
    }

    static getConfigError() {
        const tenantId = getAzureConfig('tenantId');
        const clientId = getAzureConfig('clientId');
        if (!tenantId || !clientId) {
            return 'Microsoft sign-in is not configured. Set Tenant ID and Client ID in Settings → Integrations.';
        }
        return null;
    }

    static async getClient() {
        const configError = AzureAdAuthService.getConfigError();
        if (configError) throw new Error(configError);

        const tenantId = getAzureConfig('tenantId');
        const clientId = getAzureConfig('clientId');

        if (!pca) {
            const instance = new PublicClientApplication({
                auth: {
                    clientId,
                    authority: `https://login.microsoftonline.com/${tenantId}`,
                    redirectUri: getRedirectUri(),
                    navigateToLoginRequestUrl: false
                },
                cache: {
                    cacheLocation: 'sessionStorage',
                    storeAuthStateInCookie: false
                }
            });
            try {
                await withTimeout(instance.initialize(), MSAL_TIMEOUT_MS, 'MSAL initialize');
                pca = instance;
            } catch (err) {
                console.warn('[AzureAdAuthService] MSAL initialize failed or timed out:', err?.message || err);
                throw err;
            }
        }
        return pca;
    }

    static getScopes() {
        return ['openid', 'profile', 'email', AzureAdAuthService.getApiScope()];
    }

    /**
     * Clear stuck MSAL interaction flags left behind when the user navigates away
     * (e.g. browser Back) mid redirect/logout without completing the flow.
     */
    static clearStaleInteraction() {
        const clientId = getAzureConfig('clientId');
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (!key) continue;
            const isMsalKey = key.startsWith('msal.') || (clientId && key.includes(clientId));
            if (!isMsalKey) continue;
            if (
                key.includes('interaction.status')
                || key.includes('request.params')
                || key.includes('request.origin')
                || key.includes('urlHash')
                || key.endsWith('.interaction.status')
            ) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    }

    /**
     * True only for a real MSAL/Entra redirect return.
     * Requires OAuth response params in query or hash — bare `/auth/callback`,
     * empty `?`/`#`, or leftover path fragments alone are NOT enough.
     */
    static isRedirectReturn() {
        const blob = `${window.location.search || ''}${window.location.hash || ''}`;
        // code / id_token / client_info = success; error = Entra error redirect
        return /[?&#](code|id_token|client_info|error)=/.test(blob);
    }

    /** Complete redirect after returning from Microsoft login. */
    static async handleRedirectPromise() {
        if (!AzureAdAuthService.isConfigured()) {
            return null;
        }
        // Cold boot / plain page load must not touch MSAL — only redirect returns.
        if (!AzureAdAuthService.isRedirectReturn()) {
            return null;
        }
        try {
            const client = await AzureAdAuthService.getClient();
            return await withTimeout(
                client.handleRedirectPromise(),
                MSAL_TIMEOUT_MS,
                'MSAL handleRedirectPromise'
            );
        } catch (err) {
            console.warn('[AzureAdAuthService] MSAL redirect handling failed or timed out:', err?.message || err);
            if (isInteractionInProgressError(err)) {
                AzureAdAuthService.clearStaleInteraction();
            }
            return null;
        }
    }

    /** Start Microsoft sign-in (redirect flow). */
    static async loginRedirect() {
        const client = await AzureAdAuthService.getClient();
        await client.handleRedirectPromise().catch(() => null);

        const request = {
            scopes: AzureAdAuthService.getScopes(),
            prompt: 'select_account'
        };

        try {
            await client.loginRedirect(request);
        } catch (err) {
            if (!isInteractionInProgressError(err)) throw err;

            AzureAdAuthService.clearStaleInteraction();
            try {
                await client.loginRedirect(request);
            } catch (retryErr) {
                if (isInteractionInProgressError(retryErr)) {
                    throw new Error(
                        'Microsoft sign-in is still finishing from a previous attempt. Refresh the page and try again.'
                    );
                }
                throw retryErr;
            }
        }
    }

    /** Sign out of Microsoft session. */
    static async logoutRedirect() {
        if (!AzureAdAuthService.isConfigured()) return;
        try {
            const client = await AzureAdAuthService.getClient();
            await client.handleRedirectPromise().catch(() => null);
            const account = client.getActiveAccount() || client.getAllAccounts()[0];
            if (account) {
                await client.logoutRedirect({
                    account,
                    postLogoutRedirectUri: getPostLogoutRedirectUri()
                });
            }
        } catch (err) {
            if (isInteractionInProgressError(err)) {
                AzureAdAuthService.clearStaleInteraction();
            }
            console.warn('Microsoft logout failed:', err);
        }
    }
}
